import * as Styled from "./RoomFinderModal.styles";

import {
  Box,
  Button,
  LinearProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { Controller, useWatch } from "react-hook-form";

import BaseModal from "../BaseModal/BaseModal";
import { Gear } from "../../assets";
import { MeetingRoom } from "../../components";
import { Room } from "../../types/room";
import { RootState } from "../../store/store";
import { combineDayJSToUnix } from "../../utils/utils";
import dayjs from "dayjs";
import { getAvailableRooms } from "../../store/actions/roomActions";
import { useAppDispatch } from "../../store/hooks";
import { useEffect } from "react";
import { useSelector } from "react-redux";

type RoomFinderModalProps = {
  name: string;
  control: any;
  setValue: any;
  createdEvent?: any;
  open: boolean;
  onClose: () => void;
  roomFinderError: any;
  roomFinderErrorMessage: any;
  setRoomFinderError: any;
  setRoomFinderErrorMessage: any;
};

const RoomFinderModal = ({
  name,
  control,
  setValue,
  open,
  onClose,
  createdEvent = undefined,
  setRoomFinderError,
  setRoomFinderErrorMessage,
  roomFinderError,
  roomFinderErrorMessage,
}: RoomFinderModalProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { success, loading, availableRooms, error } = useSelector(
    (state: RootState) => state.room
  );

  const watchedRooms = useWatch({ control, name });
  const watchedEventOffice = useWatch({ control, name: "office" });
  const watchedEventDate = useWatch({ control, name: "eventDate" });
  const watchedStartDateTime = useWatch({ control, name: "startDateTime" });
  const watchedEndDateTime = useWatch({ control, name: "endDateTime" });

  const startDateTime = combineDayJSToUnix(
    watchedEventDate,
    watchedStartDateTime
  );
  const endDateTime = combineDayJSToUnix(watchedEventDate, watchedEndDateTime);

  const fetchAvailableRooms = () => {
    const startDate = dayjs.unix(startDateTime);
    const endDate = dayjs.unix(endDateTime);

    const isValidStartDate = startDate.isValid();
    const isValidEndDate = endDate.isValid();
    const isStartBeforeEnd = startDate.isBefore(endDate);

    const isThirtyMinuteInterval = (date: any) => {
      const minutes = date.minute();
      return minutes === 0 || minutes === 30;
    };
    const bothDatesValid = isValidStartDate && isValidEndDate;
    const bothIntervalsCorrect =
      isThirtyMinuteInterval(startDate) && isThirtyMinuteInterval(endDate);

    if (bothDatesValid && isStartBeforeEnd && bothIntervalsCorrect) {
      setRoomFinderError(false);
      setRoomFinderErrorMessage("");
      dispatch(
        getAvailableRooms({
          office: watchedEventOffice.toLowerCase(),
          eventStartDateTime: startDate.unix(),
          eventEndDateTime: endDate.unix(),
        })
      );
    } else {
      setRoomFinderError(true);
      setRoomFinderErrorMessage(
        "The selected start and/or end times are invalid. Please correct the times and try again to find available rooms."
      );
    }
  };

  useEffect(() => {
    fetchAvailableRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateTime, endDateTime, watchedEventOffice, dispatch]);

  useEffect(() => {
    if (success && availableRooms.length > 0 && watchedRooms.length > 0) {
      const roomExists = watchedRooms.every((room: Room) =>
        availableRooms.some(
          (availableRoom: Room) => availableRoom.email === room.email
        )
      );

      const isEditingSameRoom =
        createdEvent &&
        watchedRooms.every((room: Room) =>
          createdEvent.rooms.some(
            (createdRoom: Room) =>
              createdRoom.name === room.name &&
              startDateTime === createdEvent.startDateTime &&
              endDateTime === createdEvent.endDateTime
          )
        );

      if (!roomExists && !isEditingSameRoom) {
        setRoomFinderError(true);
        setRoomFinderErrorMessage(
          "The room(s) that you have previously selected are no longer available."
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableRooms, success, watchedRooms, createdEvent]);

  const generateFinderDetails = (titles: string[], values: string[]) => {
    return (
      <Box
        sx={{ display: "flex", flexDirection: "row", gap: theme.spacing(2) }}
      >
        {titles.map((title, index) => (
          <Box key={index} sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="modalSubtitle" sx={{ fontSize: "12px" }}>
              {title}
            </Typography>
            <Typography variant="modalBody">{values[index]}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      tabletWidth="80%"
      desktopWidth="940px"
    >
      <Box>
        <Styled.ModalTitleContainer>
          <Typography variant="h2" sx={{ marginBottom: theme.spacing(1) }}>
            Event Room Finder
          </Typography>
          <Typography
            variant="subtitle1"
            style={{
              color: theme.colors.neutral.darkGray,
              marginTop: theme.spacing(1),
              marginBottom: theme.spacing(2),
            }}
          >
            Once event is published, your room selection approved/denied via
            email communication
          </Typography>
          {generateFinderDetails(
            ["Event Date", "Start Time", "End Time", "Office"],
            [
              dayjs.unix(startDateTime).format("MM/DD/YYYY"),
              dayjs.unix(startDateTime).format("hh:mm A"),
              dayjs.unix(endDateTime).format("hh:mm A"),
              watchedEventOffice === "Boston & Hartford (New England)"
                ? "New England"
                : watchedEventOffice,
            ]
          )}
        </Styled.ModalTitleContainer>
        {error ? (
          <Box sx={{ marginTop: theme.spacing(5) }}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                padding: theme.spacing(5),
              }}
            >
              <Styled.GearGIF src={Gear} alt={"gear-gif"} />
              <Styled.Typography variant="h1">Whoops!</Styled.Typography>
              <Styled.Typography>
                We were unable to load rooms. Try refreshing this page or
                checking back later again.
              </Styled.Typography>
              <Button variant="contained" onClick={fetchAvailableRooms}>
                Refresh
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ marginTop: theme.spacing(5) }}>
            {watchedRooms.length > 0 && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.colors.neutral.darkGray,
                    fontStyle: "italic",
                    marginBottom: theme.spacing(1),
                  }}
                >
                  {`Selected Rooms (${watchedRooms.length})`}
                </Typography>
                <Box
                  sx={{
                    maxHeight: "192px",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {watchedRooms.map((room: Room, index: number) => (
                    <MeetingRoom
                      key={index}
                      room={room}
                      actionText={"Remove"}
                      onClick={() => {
                        const updatedRooms = watchedRooms.filter(
                          (watchedRoom: Room) =>
                            watchedRoom.email !== room.email
                        );
                        setValue(name, updatedRooms);
                        setRoomFinderError(false);
                        setRoomFinderErrorMessage("");
                      }}
                      border={`1px solid ${theme.colors.neutral.softGray}`}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {roomFinderError && (
              <Typography
                variant="body2"
                sx={{
                  marginTop: theme.spacing(1),
                  marginBottom: theme.spacing(1),
                  color: theme.colors.action.failure,
                }}
              >
                {roomFinderErrorMessage}
              </Typography>
            )}
            {loading && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: theme.spacing(5),
                }}
              >
                <Styled.SearchIcon />
                <Styled.Typography variant="h1">Hang tight!</Styled.Typography>
                <Styled.Typography>
                  We are finding rooms for your event...
                </Styled.Typography>
                <LinearProgress sx={{ width: "100%" }} />
              </Box>
            )}
            {!loading && success && availableRooms.length > 0 && (
              <Box
                sx={{
                  position: "relative",
                  height: "450px",
                  overflowY: "auto",
                  marginTop: theme.spacing(2),
                  marginBottom: theme.spacing(2),
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.colors.neutral.darkGray,
                    fontStyle: "italic",
                  }}
                >
                  Available Rooms ({availableRooms.length})
                </Typography>
                {availableRooms.map((room) => (
                  <Controller
                    key={room.email}
                    name={name}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <MeetingRoom
                        room={room}
                        actionText={"Add"}
                        onClick={() => {
                          onChange([...value, room]);
                          setRoomFinderError(false);
                          setRoomFinderErrorMessage("");
                        }}
                        border={`0px 0px 1px 0px solid ${theme.colors.neutral.softGray}`}
                        disabled={watchedRooms.some(
                          (watchedRoom: Room) =>
                            watchedRoom.email === room.email
                        )}
                      />
                    )}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </BaseModal>
  );
};

export default RoomFinderModal;
